import type {CheckoutSession, Satang} from '../api/types';
import {StorageKeys, readJson, remove, writeJson} from '../app/storage';

/**
 * The one open ReservePay checkout attempt this phone has, on disk.
 *
 * # Why this is persisted, and why it is one and not a list
 *
 * Paying **leaves the application.** The tab navigates to a page ReservePay
 * hosts, the guest may switch to a banking app from there, and whatever comes
 * back is a fresh document with no memory. Everything Q9 needs in order to say
 * something true — which checkout, for which order, for how much, and whether
 * this phone actually left — has to survive that, and `localStorage` is the only
 * thing here that does.
 *
 * One, not a list, because a guest has one bill in front of them. A second
 * attempt against the same order replaces the first, and a second attempt
 * against a *different* order replaces it too: two open checkouts on one phone
 * is a guest paying twice, and there is no design for that anywhere in the spec.
 *
 * # The idempotency key is the load-bearing field
 *
 * `POST /v1/orders/{id}/checkout-sessions` requires an `Idempotency-Key` and the
 * server replays the original response for a repeat. That is what makes "press
 * pay again" safe after a dropped 4G connection: the retry resolves to the
 * checkout ReservePay already created rather than creating a second one for the
 * same bill, which a guest could then pay twice.
 *
 * So the key is minted **before** the request and written **before** it is sent,
 * exactly as `OrderProvider` does for `order_id`. A key minted after a
 * successful response protects nothing, because the case it exists for is the
 * response never arriving.
 *
 * The same key with a *different* body is refused with `idempotency_key_reuse`,
 * so an attempt records the amount it was minted for, and a changed balance
 * starts a new attempt rather than reusing the key against a number it was never
 * spent on.
 */
export interface CheckoutAttempt {
  order_id: string;
  /** Minted once per intent, reused on every retry. See the header. */
  idempotency_key: string;
  /** The amount this key was spent on. A different amount needs a different key. */
  amount_satang: Satang;
  /** From the `201`. Null while the request has not answered — which is a state. */
  checkout_session_id: string | null;
  /** ReservePay's hosted page. Null when the server returned no URL at all. */
  redirect_url: string | null;
  /** Server clock. Ten minutes after creation, fixed, and not ours to choose. */
  expires_at: string | null;
  /**
   * Device clock, informational: when this phone navigated away to ReservePay.
   * Null means the guest never left — a checkout was created and abandoned here,
   * which is a different sentence from "you were sent to pay and came back".
   */
  handed_off_at: string | null;
}

/** Whether the record on disk is shaped like one. Storage is shared with old builds. */
function isAttempt(value: unknown): value is CheckoutAttempt {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<CheckoutAttempt>;
  return (
    typeof candidate.order_id === 'string' &&
    typeof candidate.idempotency_key === 'string' &&
    typeof candidate.amount_satang === 'number'
  );
}

/** The open attempt, or null. Never throws — storage may be disabled entirely. */
export function readAttempt(): CheckoutAttempt | null {
  const stored = readJson<unknown>(StorageKeys.checkout);
  return isAttempt(stored) ? stored : null;
}

/** The open attempt for one order, or null when the record is about another bill. */
export function readAttemptFor(orderId: string): CheckoutAttempt | null {
  const attempt = readAttempt();
  return attempt && attempt.order_id === orderId ? attempt : null;
}

export function writeAttempt(attempt: CheckoutAttempt): CheckoutAttempt {
  writeJson(StorageKeys.checkout, attempt);
  return attempt;
}

export function clearAttempt(): void {
  remove(StorageKeys.checkout);
}

/**
 * The attempt to use for a pay action, minting one if there is nothing reusable.
 *
 * Reuses the stored key **only** when it was minted for this order and this
 * amount, because those are the two things that make a replay a replay rather
 * than a `409 idempotency_key_reuse`. Anything else is a new intent and gets a
 * new key.
 *
 * `mintKey` is injected rather than imported so this stays a pure function of
 * its inputs and the tests do not have to reason about a UUID generator.
 */
export function attemptFor(
  orderId: string,
  amountSatang: Satang,
  mintKey: () => string,
): CheckoutAttempt {
  const existing = readAttempt();
  if (
    existing &&
    existing.order_id === orderId &&
    existing.amount_satang === amountSatang &&
    // A checkout ReservePay has already closed cannot be replayed into: the
    // server would hand back the same dead `redirect_url`. A fresh key is the
    // only way to a payable page.
    !isExpired(existing)
  ) {
    return existing;
  }
  return writeAttempt({
    order_id: orderId,
    idempotency_key: mintKey(),
    amount_satang: amountSatang,
    checkout_session_id: null,
    redirect_url: null,
    expires_at: null,
    handed_off_at: null,
  });
}

/** Fold a `201` (or a replay of one) into the stored attempt. */
export function recordSession(
  attempt: CheckoutAttempt,
  session: CheckoutSession,
): CheckoutAttempt {
  return writeAttempt({
    ...attempt,
    checkout_session_id: session.checkout_session_id,
    redirect_url: session.redirect_url ?? null,
    expires_at: session.expires_at ?? null,
  });
}

/** Written immediately before the tab navigates away. After is too late. */
export function recordHandoff(attempt: CheckoutAttempt, at = new Date()): CheckoutAttempt {
  return writeAttempt({...attempt, handed_off_at: at.toISOString()});
}

/**
 * Whether ReservePay has stopped accepting payment on this checkout.
 *
 * Driven from `expires_at` — the server's own field — and never from a ten-minute
 * constant of this client's, because the lifetime is a record of ReservePay's
 * behaviour rather than a setting anybody here chose, and a constant would be
 * silently wrong the day they change it.
 *
 * An attempt with no `expires_at` is **not** expired: the field is nullable in
 * the contract, and guessing "expired" would tell a guest their payment window
 * closed on the strength of a missing field.
 */
export function isExpired(attempt: CheckoutAttempt, now = Date.now()): boolean {
  if (!attempt.expires_at) return false;
  const at = Date.parse(attempt.expires_at);
  return !Number.isNaN(at) && at <= now;
}

/** Whole seconds left on the checkout, or null when there is no honest number. */
export function secondsRemaining(attempt: CheckoutAttempt, now = Date.now()): number | null {
  if (!attempt.expires_at) return null;
  const at = Date.parse(attempt.expires_at);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.floor((at - now) / 1000));
}
